import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Button, Upload, Table } from "antd"; // Import necessary components from Ant Design
import { DownloadOutlined } from "@ant-design/icons"; // Icon for Upload
import { Link, useNavigate } from "react-router-dom"; // Import useNavigate for navigation

import useResources from "../../../hooks/AdminDashboard/useResources";
import { API_URL } from "../../../utils/constants";

function InstructorCourseList({ classrooms }) {
	const { setResources, form, handleModalConfirm, filteredResources } =
		useResources();

	const [isModalVisible, setIsModalVisible] = useState(false);
	const [selectedCourse, setSelectedCourse] = useState(null);
	const [isAddResourceVisible, setIsAddResourceVisible] = useState(false);
	// const [fileList, setFileList] = useState([]); // State to manage file list
	const [expandedCourseId, setExpandedCourseId] = useState(null);

	const [materials] = useState([{ key: Date.now() }]);
	const navigate = useNavigate(); // Initialize navigate for navigation

	const columns = [
		{ title: "Resource Name", dataIndex: "name", key: "name" },
		{
			title: "Course Name",
			dataIndex: ["courseId", "name"], // Accessing the populated field
			key: "courseName",
		},
		{
			title: "Actions",
			key: "actions",
			render: (_, record) => (
				<span className="space-x-2">
					<Link
						to={`${API_URL}/resources/download/${record.fileUrl}?courseId=${record.courseId._id}`}
						download
					>
						<DownloadOutlined />
					</Link>
				</span>
			),
		},
	];

	const toggleExpand = (courseId) => {
		setExpandedCourseId((prev) => (prev === courseId ? null : courseId));
	};

	// Load resources from local storage on component mount
	useEffect(() => {
		const storedResources =
			JSON.parse(localStorage.getItem("resources")) || [];
		const groupedResources = storedResources.reduce((acc, resource) => {
			(acc[resource.courseId] = acc[resource.courseId] || []).push(
				resource
			);
			return acc;
		}, {});
		setResources(groupedResources);
	}, [setResources]);

	const showModal = (course) => {
		setSelectedCourse(course);
		setIsModalVisible(true);
	};

	const handleOk = () => {
		setIsModalVisible(false);
		setSelectedCourse(null);
	};

	const handleCancel = () => {
		setIsModalVisible(false);
		setSelectedCourse(null);
	};

	const handleGoToClass = (classroomId) => {
		navigate(`/classroom/${classroomId}`); // Navigate to the classroom page
	};

	const handleAddResource = (course) => {
		setSelectedCourse(course); // Set selected course here
		setIsAddResourceVisible(true); // Open resource modal
	};

	return (
		<div className="mt-8">
			<h3 className="text-2xl font-semibold mb-4">Instructor Courses</h3>
			{classrooms.length > 0 ? (
				classrooms.map((classroom) => (
					<div key={classroom?._id} className="mb-6">
						<div
							className="flex items-center justify-between bg-gray-100 p-4 rounded-lg shadow-md mb-4 cursor-pointer"
							onClick={() => toggleExpand(classroom?._id)}
						>
							{/* Course Info */}
							<div className="flex flex-col flex-grow gap-1">
								<h4 className="text-lg font-semibold text-gray-800">
									{classroom?.courseId?.name}
								</h4>
								<p className="text-sm text-gray-600">
									{classroom?.courseId?.description}
								</p>
							</div>

							{/* Actions */}
							<div className="flex gap-2 ml-4">
								<button
									onClick={(e) => {
										e.stopPropagation(); // Prevent parent click event
										showModal(classroom.courseId);
									}}
									className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-300 text-sm"
								>
									Details
								</button>
								<button
									onClick={(e) => {
										e.stopPropagation(); // Prevent parent click event
										handleGoToClass(classroom?._id);
									}}
									className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition duration-300 text-sm"
								>
									Go to Class
								</button>
								<button
									onClick={(e) => {
										e.stopPropagation(); // Prevent parent click event
										handleAddResource(classroom?.courseId);
									}}
									className="bg-orange-500 text-white py-2 px-4 rounded-md hover:bg-orange-600 transition duration-300 text-sm"
								>
									Add Resource
								</button>
							</div>
						</div>

						{/* Resources Section */}
						{expandedCourseId === classroom?._id && (
							<div className="mt-4">
								<h5 className="text-lg font-semibold mb-4">
									Resources:
								</h5>
								<Table
									columns={columns}
									dataSource={
										Array.isArray(filteredResources)
											? filteredResources.filter(
													(resource) =>
														resource.courseId
															?.name ===
														classroom?.courseId
															?.name
											  )
											: []
									}
									rowKey="id"
									pagination={{ position: ["bottomRight"] }}
									className="w-full"
								/>
							</div>
						)}
					</div>
				))
			) : (
				<p>No courses found</p>
			)}

			{/* Modal for Course Details */}
			<Modal
				title={selectedCourse?.name}
				open={isModalVisible}
				onOk={handleOk}
				onCancel={handleCancel}
			>
				{selectedCourse && (
					<>
						<p>
							<strong>Description:</strong>{" "}
							{selectedCourse.description}
						</p>
						<p>
							<strong>Schedule:</strong>{" "}
							{selectedCourse.schedule.join(", ")}
						</p>
					</>
				)}
			</Modal>

			{/* Modal for Adding Resources */}
			<Modal
				title={`Add Resource for ${selectedCourse?.name}`}
				open={isAddResourceVisible}
				onCancel={() => setIsAddResourceVisible(false)}
				footer={null}
			>
				<Form
					layout="vertical"
					onFinish={handleModalConfirm}
					form={form}
					encType="multipart/form-data"
				>
					{/* Display the Selected Course Name */}
					<Form.Item label="Course">
						<p className="text-gray-700">{selectedCourse?.name}</p>
						<Form.Item
							name="courses"
							initialValue={selectedCourse?._id}
							hidden
						>
							<Input type="hidden" />
						</Form.Item>
					</Form.Item>

					{/* Material Upload Section */}
					{materials.map((resource) => (
						<div
							key={resource.key}
							className="border p-4 rounded-lg shadow-md bg-gray-50 mb-4 relative"
						>
							<Form.Item
								name={`files-${resource.key}`}
								label="File Upload"
								rules={[
									{
										required: true,
										message: "Please upload a file",
									},
								]}
								getValueFromEvent={(e) => e.fileList}
							>
								<Upload
									beforeUpload={() => false}
									listType="text"
									accept="*/*"
									multiple
								>
									<input
										type="file"
										accept="*/*"
										className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-100 focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-indigo-100 file:text-indigo-700 hover:file:bg-indigo-200"
										multiple
									/>
								</Upload>
							</Form.Item>
						</div>
					))}

					{/* Submit Button */}
					<Form.Item>
						<Button type="primary" htmlType="submit">
							Submit
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
}

export default InstructorCourseList;
